// Ensemble eligibility (docs/2027_registration_rules.md → "Ensemble Division — rules").
//
// Shared by the server (api/_lib/ensembleRules.js — team pages and finalize)
// and the browser (the school portal's live check while picking students).
// Plain CommonJS; depends only on ./ageRules.
const { ageAsOf, POINTE_EXPECTED_FROM_AGE, POINTE_ALLOWED_FROM_AGE, POINTE_DISCOURAGED_BELOW_AGE } = require('./ageRules');

const MIN_DANCERS = 3;
const MAX_DANCERS = 8;

const DIVISIONS = {
  junior: 'Junior Ensemble',
  intermediateSenior: 'Intermediate/Senior Ensemble',
  adult: 'Adult Ensemble',
};

const listNames = (members) => members.map((m) => m.name).join(', ');

/**
 * members: [{ name, dateOfBirth, footwear }]
 * Returns { size, averageAge, division, eligible, problems, warnings }.
 * `problems` block finalize; `warnings` never do. `division` is the age group
 * the team falls into (null if none fits); final divisions may be merged later.
 */
function checkEnsemble(members, year) {
  const dancers = members.map((m) => ({ ...m, age: ageAsOf(m.dateOfBirth, year) }));
  const n = dancers.length;
  const problems = [];
  const warnings = [];

  if (n < MIN_DANCERS) problems.push(`An ensemble needs at least ${MIN_DANCERS} dancers (currently ${n}).`);
  if (n > MAX_DANCERS) problems.push(`An ensemble can have at most ${MAX_DANCERS} dancers (currently ${n}).`);
  if (n === 0) return { size: 0, averageAge: null, division: null, eligible: false, problems, warnings };

  const total = dancers.reduce((sum, d) => sum + d.age, 0);
  const averageAge = total / n;
  const placement = Math.floor(averageAge);

  let division = null;
  if (placement < 7) {
    problems.push('The average age is below 7, so this ensemble is not eligible.');
  } else if (placement <= 11) {
    division = DIVISIONS.junior;
    const tooYoung = dancers.filter((d) => d.age < 7);
    if (tooYoung.length) problems.push(`All Junior Ensemble dancers must be 7 or older: ${listNames(tooYoung)}.`);
  } else if (placement <= 17) {
    division = DIVISIONS.intermediateSenior;
  } else {
    const minors = dancers.filter((d) => d.age < 18);
    if (minors.length) {
      problems.push(
        `The average age is 18 or over, which needs every dancer to be 18+, but ${listNames(minors)} ` +
        `${minors.length === 1 ? 'is' : 'are'} under 18. This team doesn't fit any division.`
      );
    } else {
      division = DIVISIONS.adult;
    }
  }

  const isYouth = division !== DIVISIONS.adult && placement < 18;

  // Youth and adults don't mix: every dancer in a youth ensemble is 17 or
  // younger on Jan 1 (adult ensembles already require everyone to be 18+).
  if (division && isYouth) {
    const adults = dancers.filter((d) => d.age >= 18);
    if (adults.length) {
      problems.push(
        `Youth ensembles are for dancers 17 and under (age on January 1, ${year}); ` +
        `${listNames(adults)} ${adults.length === 1 ? 'is' : 'are'} 18 or older.`
      );
    }
  }

  // Age-difference rule, youth only: |n·a − T| ≤ 4n (exact average, no rounding).
  if (isYouth && placement >= 7) {
    const outside = dancers.filter((d) => Math.abs(n * d.age - total) > 4 * n);
    if (outside.length) {
      problems.push(
        `No dancer may be more than 4 years older or younger than the average age (${averageAge.toFixed(1)}): ` +
        `${outside.map((d) => `${d.name} (${d.age})`).join(', ')}.`
      );
    }
  }

  if (isYouth) {
    const softTwelvePlus = dancers.filter((d) => d.footwear === 'soft' && d.age >= POINTE_EXPECTED_FROM_AGE);
    if (softTwelvePlus.length) {
      warnings.push(
        `Dancers 12 and over are expected on pointe in this division; judges will deduct technique points for soft shoes: ` +
        `${listNames(softTwelvePlus)}.`
      );
    }
    const pointe = dancers.filter((d) => d.footwear === 'pointe');
    const tooYoung = pointe.filter((d) => d.age < POINTE_ALLOWED_FROM_AGE);
    if (tooYoung.length) {
      warnings.push(`Dancers 10 and under can't dance on pointe. Please choose soft shoes: ${listNames(tooYoung)}.`);
    }
    const eleven = pointe.filter((d) => d.age >= POINTE_ALLOWED_FROM_AGE && d.age < POINTE_DISCOURAGED_BELOW_AGE);
    if (eleven.length) {
      warnings.push(`Pointe is strongly discouraged at age 11; we recommend soft shoes: ${listNames(eleven)}.`);
    }
  }

  return {
    size: n,
    averageAge: Math.round(averageAge * 10) / 10,
    division,
    eligible: problems.length === 0,
    problems,
    warnings,
  };
}

module.exports = { checkEnsemble, DIVISIONS, MIN_DANCERS, MAX_DANCERS };
